/**
 * Probe the sync socket when the page returns from a state that likely left
 * the underlying TCP half-open: a long-hidden tab returning to foreground
 * (laptop sleep / phone unlock), or the browser reporting `online` again.
 *
 * Why this exists: the browser WebSocket API exposes no protocol-level
 * ping/pong (JS can't probe liveness), and `onclose` won't fire for a half-open
 * socket — `readyState` stays at OPEN even after the OS has torn down TCP.
 *
 * Probe shape: send SYNC_PING, watch for any inbound frame (the CF edge
 * answers with SYNC_PONG via `setWebSocketAutoResponse` without waking the DO;
 * a real sync message counts too). If nothing arrives in `probeTimeout` ms,
 * force-close — the upstream adapter's onclose handler reconnects.
 *
 * Reconnect is only forced when the probe actually fails, so a healthy
 * resume pays nothing beyond a single ping round-trip.
 */
import { SYNC_PING } from "../sync-keepalive";

export type SocketResumeMonitorOptions = {
  /** Tab must be hidden this long before visibility-visible triggers a probe. Default: 30_000. */
  staleHiddenThreshold?: number;
  /** ms to wait for any inbound frame after probing. Default: 5_000. */
  probeTimeout?: number;
};

/** Anything holding a (re)connectable socket — a plain holder object or a
 *  reconnecting adapter whose `socket` field turns over. */
export type SocketHolder = { socket?: WebSocket };

export class SocketResumeMonitor {
  #adapter: SocketHolder;
  #threshold: number;
  #probeTimeout: number;
  #hiddenSince?: number;

  #onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      this.#hiddenSince = Date.now();
      return;
    }
    const hiddenFor = this.#hiddenSince ? Date.now() - this.#hiddenSince : 0;
    this.#hiddenSince = undefined;
    if (hiddenFor >= this.#threshold) this.#probe();
  };

  #onOnline = () => this.#probe();

  constructor(adapter: SocketHolder, opts?: SocketResumeMonitorOptions) {
    this.#adapter = adapter;
    this.#threshold = opts?.staleHiddenThreshold ?? 30_000;
    this.#probeTimeout = opts?.probeTimeout ?? 5_000;
  }

  start() {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.#onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("online", this.#onOnline);
    }
  }

  stop() {
    if (typeof document !== "undefined") {
      document.removeEventListener(
        "visibilitychange",
        this.#onVisibilityChange,
      );
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("online", this.#onOnline);
    }
  }

  #currentSocket(): WebSocket | undefined {
    return this.#adapter.socket;
  }

  #probe() {
    const socket = this.#currentSocket();
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    let received = false;
    const onMessage = () => {
      received = true;
      socket.removeEventListener("message", onMessage);
    };
    socket.addEventListener("message", onMessage);

    try {
      socket.send(SYNC_PING);
    } catch {
      // send() threw — socket is hosed already. Skip the wait.
      socket.removeEventListener("message", onMessage);
      this.#forceReconnect(socket);
      return;
    }

    setTimeout(() => {
      socket.removeEventListener("message", onMessage);
      if (received) return;
      console.warn("automerge-ws: resume probe timed out, forcing reconnect");
      this.#forceReconnect(socket);
    }, this.#probeTimeout);
  }

  #forceReconnect(socket: WebSocket) {
    if (socket.readyState === WebSocket.OPEN) socket.close();
  }
}
